// Ogma Connect paths, phase 1 (docs/specs/CONNECT.md sections 4 to 6; steps 9.13 to 9.15; migration 20261122090000).
// The templates and blocks as the database knows them (crm__connect_templates, crm__connect_blocks), the shapes of the
// two reads (crm_connect_paths_board, crm_connect_run), the words the screens use, and the rules of the paths editor on
// Conversations › Routes. A saved edit is a settings change (connect_paths) the owner approves, like every other form.
// Campaigns as the spine (19 Oct 2026, migration 20261229090000): a path lists the campaigns that use it (several may
// share one; each campaign is on one path at most), and the path of the firm's campaign comes first when a run starts.
import { connectSettings, FLAVOURS, hoursText, type Flavour, type SellingMethod } from "./connect";
import { checkSection } from "./settingsForms";
import { OFFERS, PATH_TEMPLATES } from "./settingsRules";
import type { JsonObject } from "./types";
import { cleanRoutes, type Route } from "./pathRoutes";

export type BlockKey =
  | "qualify" | "gather" | "book" | "meet" | "recap" | "proposal" | "quote" | "accept" | "pay" | "sign_up"
  | "grant_access" | "nudge" | "convert" | "bid_decision" | "prepare" | "submit" | "follow_up" | "send_document" | "make_offer";
export type TemplateKey = "intro_call" | "instant_price" | "quote_from_known" | "survey_then_quote" | "trial_access" | "tender_response";
export type Offer = (typeof OFFERS)[number];

export interface BlockInfo {
  label: string;
  /** watched: the run moves on when Ogma sees it happen; person: a task for a person until a later phase. */
  kind: "watched" | "person";
  /** The phase of Connect that automates it (or adds to it); null when it is built, or stays a person by design. */
  automated_in: string | null;
  what: string;
}

/** crm__connect_blocks (keep in step with the migration and engine/paths.py BLOCKS). */
export const BLOCKS: Record<BlockKey, BlockInfo> = {
  qualify: { label: "Qualify", kind: "person", automated_in: "phase 4", what: "Ask the few questions that decide the next step, using what you already know about them." },
  gather: { label: "Gather the details", kind: "person", automated_in: null, what: "With a question flow on this step, Ogma sends them their link on the quote portal and moves on when they answer. Without one, read what the price needs from their enquiry, chat or email and ask only for what is missing." },
  book: { label: "Book", kind: "watched", automated_in: null, what: "A meeting (call, video or site visit) is booked with them. Ogma offers times or your booking link when they reach this step." },
  meet: { label: "Meet", kind: "watched", automated_in: "phase 5", what: "The meeting is held." },
  recap: { label: "Email after the call", kind: "watched", automated_in: null, what: "The email after the meeting goes to them (9.1): the actions agreed, a highlight from the call, or a document such as the quote." },
  proposal: { label: "Proposal", kind: "watched", automated_in: null, what: "A written proposal goes to them (9.2)." },
  quote: { label: "Quote", kind: "person", automated_in: null, what: "Make the quote with Make a quote on the firm page, or check the one Ogma priced, and send it. This step is done when the quote goes to them." },
  accept: { label: "Accept", kind: "watched", automated_in: null, what: "They say yes: they accept the quote on its page, a document is signed, or the deal is won." },
  pay: { label: "Payment", kind: "person", automated_in: "phase 3", what: "Take the deposit or payment, or set up the Direct Debit." },
  sign_up: { label: "Sign-up", kind: "person", automated_in: "phase 3", what: "Sign them up for the trial or for access." },
  grant_access: { label: "Give access", kind: "person", automated_in: "phase 3", what: "Give them access to the product or service." },
  nudge: { label: "Help during the trial", kind: "person", automated_in: "phase 3", what: "Check in during the trial and help them get value from it." },
  convert: { label: "Convert to paid", kind: "person", automated_in: "phase 3", what: "Turn the trial into a paid plan." },
  bid_decision: { label: "Bid or not", kind: "person", automated_in: null, what: "Decide whether to bid." },
  prepare: { label: "Prepare the response", kind: "person", automated_in: null, what: "Prepare the tender or RFP response." },
  submit: { label: "Submit", kind: "person", automated_in: null, what: "Submit the response by the formal route only." },
  follow_up: { label: "Follow up", kind: "watched", automated_in: null, what: "Chasers, objections and the save offer until they decide (9.8 to 9.12)." },
  // Steps Ogma does itself (migration 20270305100000).
  send_document: { label: "Send a document", kind: "watched", automated_in: null, what: "Ogma sends them one of your documents, or their quote, with a short covering note in your style (9.30). Done when it has gone." },
  make_offer: { label: "Make an offer", kind: "watched", automated_in: null, what: "If they go quiet, Ogma makes an offer within your save offer's limits, which you approve unless offers send by themselves (9.31). Done when they answer or the offer runs out." },
};

export interface TemplateInfo {
  key: TemplateKey;
  label: string;
  for: string;
  blocks: { key: BlockKey; optional: boolean }[];
}

/** crm__connect_templates, in their order (keep in step with the migration and settingsRules PATH_TEMPLATES). */
export const TEMPLATES: TemplateInfo[] = [
  { key: "intro_call", label: "Intro call", for: "B2B, consultative sales", blocks: [{ key: "qualify", optional: true }, { key: "book", optional: false }, { key: "meet", optional: false }, { key: "recap", optional: false }, { key: "proposal", optional: true }, { key: "accept", optional: true }, { key: "follow_up", optional: false }] },
  { key: "instant_price", label: "Instant price", for: "Insurance, standard products, simple services", blocks: [{ key: "qualify", optional: true }, { key: "quote", optional: false }, { key: "accept", optional: false }, { key: "pay", optional: true }, { key: "follow_up", optional: false }] },
  { key: "quote_from_known", label: "Quote from what they told us", for: "Services where the enquiry already holds the detail", blocks: [{ key: "gather", optional: false }, { key: "quote", optional: false }, { key: "accept", optional: false }, { key: "pay", optional: true }, { key: "follow_up", optional: false }] },
  { key: "survey_then_quote", label: "Survey, then quote", for: "Trades, installs, site work", blocks: [{ key: "book", optional: false }, { key: "meet", optional: false }, { key: "quote", optional: false }, { key: "accept", optional: false }, { key: "pay", optional: true }, { key: "follow_up", optional: false }] },
  { key: "trial_access", label: "Trial or access", for: "Software, memberships, courses", blocks: [{ key: "sign_up", optional: false }, { key: "grant_access", optional: false }, { key: "nudge", optional: false }, { key: "convert", optional: false }, { key: "follow_up", optional: false }] },
  { key: "tender_response", label: "Tender response", for: "Public and private RFPs", blocks: [{ key: "bid_decision", optional: false }, { key: "prepare", optional: false }, { key: "submit", optional: false }, { key: "follow_up", optional: false }] },
];
export const TEMPLATE_BY_KEY = Object.fromEntries(TEMPLATES.map((t) => [t.key, t])) as Record<TemplateKey, TemplateInfo>;

/** The words for each CTA offer when it picks a path. */
export const OFFER_PATH_LABEL: Record<Offer, string> = {
  consult: "Book a call",
  quote: "Ask for a quote",
  guide: "Free guide",
  scorecard: "Scorecard",
  subscribe: "Newsletter",
  visit: "Visit us",
  call: "Phone us",
};

// ------------------------------------------------------------------ the reads
export interface BoardBlock {
  key: BlockKey;
  label: string;
  kind: "watched" | "person";
  optional: boolean;
  off: boolean;
  automated_in?: string | null;
  firms: number;
}

export interface WaitingRun {
  run_id: string;
  organisation_id: string;
  organisation: string;
  deal_id?: string | null;
  block: BlockKey;
  block_label: string;
  since: string;
  task_id?: string | null;
  /** The open "choose where they go next" task, when a route asks a person (9.25). */
  route_task?: string | null;
  /** The campaign the firm came through (connect_runs.play_id) and its name. */
  play_id?: string | null;
  campaign?: string | null;
}

// ------------------------------------------------------------------ step settings (migration 20261231090000)
/** The keys a step can have: "reply" (the first reply to an enquiry, before the path's blocks) or a block. */
export type StepKey = "reply" | BlockKey;
export type ReplyMode = "acknowledge" | "booking_link" | "offer_times" | "confirm_booking" | "send_guide";
export type BookMode = "send_link" | "propose_times" | "one_time";
export type QuoteMode = "price_book" | "by_hand";
/** What the email after the call is for (migration 20270209090000). */
export type RecapMode = "actions" | "highlight" | "document";
export type Channel = "whatsapp" | "email";

/** A path's save offer, any field of which overrides connect.save_offer for that path. */
export type StepSaveOffer = {
  enabled?: boolean;
  kinds?: ("discount" | "smaller_first_job" | "payment_terms" | "trial")[];
  max_discount_pct?: number;
  approval?: "always" | "never" | "above";
  approve_above_pct?: number;
  decide_after_days?: number;
}

/** "If nothing happens" at a waiting step (migration 20270219090000; docs/specs/CONNECT.md section 20). */
export type ChaseChannel = "email" | "whatsapp" | "sms" | "call_task";
export type ChaseThen = "task" | "move_on" | "lost";
export type ChaseStep = {
  /** Days after the step's start (the first) or the last chaser (the rest): 0 to 30. */
  gap_days: number;
  channel: ChaseChannel;
  /** What this chaser should say, for the writer (up to 300 characters). */
  brief?: string;
}
export type ChaseSettings = {
  /** Hours after the firm reaches the step before the first chaser (0.25 to 720); else the step's pace. */
  after_hours?: number;
  steps?: ChaseStep[];
  /** After the last chaser: a task for a person (the default), move on, or lost. */
  then?: ChaseThen;
}
/** The steps where Ogma waits for the firm and can chase it (crm__chase_blocks; engine/paths.py CHASE_BLOCKS): Qualify
 * and Gather wait for the answers to the question flow they send (migration 20270224090000). The email after the call
 * keeps its own chase_days and chases. */
export const CHASE_BLOCKS: readonly BlockKey[] = ["qualify", "gather", "book", "meet", "proposal", "accept", "follow_up"];
export const CHASE_CHANNELS: readonly ChaseChannel[] = ["email", "whatsapp", "sms", "call_task"];
export const CHASE_THEN: readonly ChaseThen[] = ["task", "move_on", "lost"];

/** crm__clean_chase */
export function cleanChase(raw: unknown): ChaseSettings | null {
  if (!plainObject(raw)) return null;
  const out: ChaseSettings = {};
  const after = stepNum(raw.after_hours, 0.25, 720);
  if (after !== null) out.after_hours = after;
  if (Array.isArray(raw.steps)) {
    const steps: ChaseStep[] = [];
    for (const s of raw.steps) {
      if (steps.length >= 3) break;
      if (!plainObject(s)) continue;
      const gap = stepNum(s.gap_days, 0, 30, true);
      if (gap === null || typeof s.channel !== "string" || !(CHASE_CHANNELS as readonly string[]).includes(s.channel)) continue;
      const step: ChaseStep = { gap_days: gap, channel: s.channel as ChaseChannel };
      if (typeof s.brief === "string" && s.brief.trim()) step.brief = s.brief.trim().slice(0, 300);
      steps.push(step);
    }
    out.steps = steps;
  }
  if (typeof raw.then === "string" && (CHASE_THEN as readonly string[]).includes(raw.then)) out.then = raw.then as ChaseThen;
  return Object.keys(out).length ? out : null;
}

/** What a person may say when marking a step done (crm__person_outcomes): Qualify's fit. */
export type PathOutcome = "fit" | "not_fit";

/** One step's settings (connect.paths[].steps[key]); which fields a step may hold is STEP_SPEC. */
export type StepSettings = {
  mode?: ReplyMode | BookMode | QuoteMode | RecapMode;
  /** How quickly the step must happen, in hours (0.25 to 720): the reply due time and the step's task due time. */
  due_hours?: number;
  /** The first reply that thanks them: the time it promises a fuller answer in, in hours (0.25 to 720). */
  promise_hours?: number;
  channels?: Channel[];
  whatsapp_template?: string;
  meeting_type?: string;
  times?: number;
  guide_url?: string;
  options?: string[];
  save_offer?: StepSaveOffer;
  /** The email after the call: the document it shares ("quote", or a key in connect.documents) and what to highlight. */
  document?: string;
  note?: string;
  /** Qualify and Gather: the question flow sent to the customer (its key); Quote: the price book (its key). */
  flow?: string;
  book?: string;
  /** If they go quiet after it: a chaser every chase_days (1 to 30), at most chases (0 to 5; 0 for none). */
  chase_days?: number;
  chases?: number;
  /** Send a document and Make an offer: days before Ogma acts (0 to 60; an offer waits that long with no answer). */
  wait_days?: number;
  /** Make an offer: days the offer lasts (1 to 60). */
  expires_days?: number;
  /** "If nothing happens" on a waiting step (CHASE_BLOCKS only). */
  chase?: ChaseSettings;
}
export type PathSteps = Partial<Record<StepKey, StepSettings>>;

/** crm__path_step_spec (keep in step with the migration and engine/paths.py STEP_SPEC). A block not listed takes
 * due_hours only. */
export const STEP_SPEC: Record<string, { modes: string[]; fields: (keyof StepSettings)[]; max_times?: number }> = {
  // promise_hours: the time the first reply promises a fuller answer in (migration 20270312090000)
  reply: { modes: ["acknowledge", "booking_link", "offer_times", "confirm_booking", "send_guide"], fields: ["mode", "due_hours", "channels", "whatsapp_template", "meeting_type", "times", "guide_url", "promise_hours"], max_times: 3 },
  book: { modes: ["send_link", "propose_times", "one_time"], fields: ["mode", "due_hours", "meeting_type", "times"], max_times: 4 },
  recap: { modes: ["actions", "highlight", "document"], fields: ["mode", "due_hours", "document", "note", "chase_days", "chases"] },
  // Customer quoting (migration 20270221090000): Qualify and Gather send a question flow, Quote picks a price book.
  qualify: { modes: [], fields: ["due_hours", "flow"] },
  gather: { modes: [], fields: ["due_hours", "flow"] },
  quote: { modes: ["price_book", "by_hand"], fields: ["mode", "due_hours", "options", "book"] },
  // Send a document and Make an offer (migration 20270305100000).
  send_document: { modes: [], fields: ["due_hours", "document", "note", "wait_days"] },
  make_offer: { modes: [], fields: ["due_hours", "wait_days", "expires_days", "note", "save_offer"] },
  follow_up: { modes: [], fields: ["due_hours", "save_offer"] },
};
const OTHER_STEP: { modes: string[]; fields: (keyof StepSettings)[]; max_times?: number } = { modes: [], fields: ["due_hours"] };
export function stepSpec(key: string) {
  return STEP_SPEC[key] ?? OTHER_STEP;
}
const TYPE_RE = /^[a-z][a-z0-9_]{1,40}$/;
export const ENQUIRY_REPLY_RE = /^enquiry_reply(_[a-z0-9_]{1,26})?$/;
const GUIDE_RE = /^https:\/\/[^\s<>"']+$/;
const OPTION_RE = /^[a-z][a-z0-9_]{0,39}$/;
const SAVE_KINDS = ["discount", "smaller_first_job", "payment_terms", "trial"];

function stepNum(v: unknown, lo: number, hi: number, whole = false): number | null {
  if (typeof v !== "number" || !Number.isFinite(v) || v < lo || v > hi) return null;
  if (whole && !Number.isInteger(v)) return null;
  return v;
}

function stepList(v: unknown, ok: (s: string) => boolean, most: number): string[] | null {
  if (!Array.isArray(v)) return null;
  const out: string[] = [];
  for (const x of v) if (typeof x === "string" && ok(x) && !out.includes(x)) out.push(x);
  return out.length ? out.slice(0, most) : null;
}

function plainObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

/** crm__clean_path_steps: the steps Ogma keeps for a path whose blocks in force are `blocks` (the same in
 * engine/paths.py clean_steps; supabase/tests/fixtures/paths_clean_cases.json holds the shared cases). */
export function cleanSteps(raw: unknown, blocks: readonly string[]): PathSteps {
  const out: Record<string, StepSettings> = {};
  if (!plainObject(raw)) return out;
  for (const [key, v] of Object.entries(raw)) {
    if (!plainObject(v) || (key !== "reply" && !blocks.includes(key))) continue;
    const spec = stepSpec(key);
    const has = (f: keyof StepSettings) => spec.fields.includes(f);
    const step: StepSettings = {};
    if (has("mode") && typeof v.mode === "string" && spec.modes.includes(v.mode)) step.mode = v.mode as StepSettings["mode"];
    const due = stepNum(v.due_hours, 0.25, 720);
    if (due !== null) step.due_hours = due;
    if (has("promise_hours")) {
      const promise = stepNum(v.promise_hours, 0.25, 720);
      if (promise !== null) step.promise_hours = promise;
    }
    if (has("times")) {
      const n = stepNum(v.times, 1, spec.max_times ?? 1, true);
      if (n !== null) step.times = n;
    }
    if (has("meeting_type") && typeof v.meeting_type === "string" && TYPE_RE.test(v.meeting_type)) step.meeting_type = v.meeting_type;
    if (has("whatsapp_template") && typeof v.whatsapp_template === "string" && ENQUIRY_REPLY_RE.test(v.whatsapp_template)) step.whatsapp_template = v.whatsapp_template;
    if (has("guide_url") && typeof v.guide_url === "string" && GUIDE_RE.test(v.guide_url) && v.guide_url.length <= 300) step.guide_url = v.guide_url;
    if (has("channels")) {
      const ch = stepList(v.channels, (x) => x === "whatsapp" || x === "email", 2);
      if (ch) step.channels = ch as Channel[];
    }
    if (has("options")) {
      const op = stepList(v.options, (x) => OPTION_RE.test(x), 3);
      if (op) step.options = op;
    }
    if (has("flow") && typeof v.flow === "string" && OPTION_RE.test(v.flow)) step.flow = v.flow;
    if (has("book") && typeof v.book === "string" && OPTION_RE.test(v.book)) step.book = v.book;
    if (has("document") && typeof v.document === "string" && TYPE_RE.test(v.document)) step.document = v.document;
    if (has("note") && typeof v.note === "string" && v.note.trim()) step.note = v.note.trim().slice(0, 300);
    if (has("chase_days")) {
      const n = stepNum(v.chase_days, 1, 30, true);
      if (n !== null) step.chase_days = n;
    }
    if (has("chases")) {
      const n = stepNum(v.chases, 0, 5, true);
      if (n !== null) step.chases = n;
    }
    if (has("wait_days")) {
      const n = stepNum(v.wait_days, 0, 60, true);
      if (n !== null) step.wait_days = n;
    }
    if (has("expires_days")) {
      const n = stepNum(v.expires_days, 1, 60, true);
      if (n !== null) step.expires_days = n;
    }
    if (has("save_offer") && plainObject(v.save_offer)) {
      const so = v.save_offer;
      const o: StepSaveOffer = {};
      if (typeof so.enabled === "boolean") o.enabled = so.enabled;
      const kinds = stepList(so.kinds, (x) => SAVE_KINDS.includes(x), 4);
      if (kinds) o.kinds = kinds as StepSaveOffer["kinds"];
      const max = stepNum(so.max_discount_pct, 0, 100);
      if (max !== null) o.max_discount_pct = max;
      if (typeof so.approval === "string" && ["always", "never", "above"].includes(so.approval)) o.approval = so.approval as StepSaveOffer["approval"];
      const above = stepNum(so.approve_above_pct, 0, 100);
      if (above !== null) o.approve_above_pct = above;
      const days = stepNum(so.decide_after_days, 1, 60, true);
      if (days !== null) o.decide_after_days = days;
      if (Object.keys(o).length) step.save_offer = o;
    }
    if ((CHASE_BLOCKS as readonly string[]).includes(key)) {
      const chase = cleanChase(v.chase);
      if (chase) step.chase = chase;
    }
    if (Object.keys(step).length) out[key] = step;
  }
  return out;
}

/** A cleaned path (crm__connect_paths), as the settings hold it plus its blocks in force. */
export interface PathSetting {
  key: string;
  label: string;
  template: TemplateKey;
  offers: Offer[];
  flavours: Flavour[];
  /** The campaigns (play ids) that use this path; several may share it, each campaign is on one path at most. */
  campaigns: string[];
  off: BlockKey[];
  default: boolean;
  /** The Inbound path: firms with no campaign follow it (one path at most; migration 20270131090000). */
  inbound?: boolean;
  blocks?: BlockKey[];
  /** Settings per step (connect.paths[].steps; migration 20261231090000). Empty when none are set. */
  steps?: PathSteps;
  /** Where a firm goes after a step (step 9.25; migration 20270219090000); absent when the path has none. */
  routes?: Route[];
}

/** The modules whose campaigns can have a path (crm__campaign_modules), in the order the screens group them. */
export const CAMPAIGN_MODULES = ["attract", "find", "network", "reconnect"] as const;
export type CampaignModule = (typeof CAMPAIGN_MODULES)[number];
export const CAMPAIGN_MODULE_LABEL: Record<CampaignModule, string> = { attract: "Attract", find: "Find", network: "Network", reconnect: "ReConnect" };
/** The modules a person makes campaigns in on Connect (Find's are on Prospects › Campaigns). */
export const MADE_HERE: readonly CampaignModule[] = ["attract", "network", "reconnect"];

/** A campaign as crm_campaigns_all lists it. */
export interface CampaignSummary {
  id: string;
  module: CampaignModule;
  name: string;
  status: "approved" | "paused" | "proposed" | "draft";
  /** The path that lists it, or absent when none does (the firm's offer, way in or the default then decide). */
  path_key?: string | null;
  /** Live Connect runs of firms that came through it. */
  firms: number;
  /** With no path of its own: the path its firms get (unless the offer they answered has one), and why. */
  falls_back_to?: { path_key: string; path_label: string; chosen_by: "flavour" | "default" };
}

export interface BoardPath extends PathSetting {
  template_label: string;
  /** The campaigns this path lists, by name. */
  campaign_list?: { id: string; module: CampaignModule; name: string; status: string }[];
  /** Campaigns the settings still list on this path that have been retired: shown in the editor to untick. */
  retired_campaigns?: { id: string; module: CampaignModule; name: string }[];
  /** Live runs on this path by the campaign they came through (no play_id: none). */
  by_campaign?: { play_id?: string | null; campaign?: string | null; firms: number }[];
  firms: number;
  /** Live runs on a path made from `selling` (none saved) that sit at a person's step with no task: Ogma tracks
   * them but makes no task until the paths are saved. Counted in `firms`, left out of `waiting`. */
  tracked_only?: number;
  block_list: BoardBlock[];
  waiting: WaitingRun[];
  /** Firms past their step's pace, by step (migration 20270219090000). */
  late?: Partial<Record<BlockKey, number>>;
  /** The last time firms on it took new steps ("Firms on it now too"), and how many moved. */
  rerouted?: { at: string; moved: number } | null;
}

export interface BookingAutonomy {
  kind: "meeting_email";
  level: string;
  on: boolean;
  kill: boolean;
  changed_at?: string | null;
  changed_by?: string | null;
  reason?: string | null;
}

/** crm_connect_paths_board */
export interface PathsBoard {
  derived: boolean;
  paths: BoardPath[];
  other_runs: { path_key: string; path_label: string; firms: number }[];
  templates: { key: TemplateKey; label: string; for: string; blocks: { key: BlockKey; label: string; kind: "watched" | "person"; optional: boolean }[] }[];
  blocks?: Record<string, BlockInfo>;
  offers?: string[];
  /** Every campaign in Attract, Find, Network and ReConnect, with its path (crm_campaigns_all). */
  campaigns?: CampaignSummary[];
  /** The key of the Inbound path, if one is set. */
  inbound?: string | null;
  booking_autonomy: BookingAutonomy;
  /** What the step editor can pick from (migration 20261231090000). */
  step_choices?: StepChoices;
}

/** crm_connect_paths_board step_choices: the meeting types, the enquiry reply and its versions, the published price
 * book's options, the guides from your calls to action, and whether replies have a booking link. */
export interface StepChoices {
  meeting_types: { key: string; label: string; minutes?: number; path?: string | null; derived?: boolean }[];
  whatsapp_templates: { key: string; name: string; approved: boolean }[];
  price_book: { version: number; options: { key: string; label: string; tier?: string }[] } | null;
  guides: { label: string; url: string }[];
  booking_link: boolean;
  /** The business's documents the email after the call can share (connect.documents, migration 20270209090000). */
  documents?: ShareDocument[];
  /** The published question flows Qualify and Gather can send, and the price books (with a published version) Quote
   * can use (migration 20270221090000). */
  flows?: { key: string; name: string; purpose: "qualify" | "quote"; book_key?: string }[];
  books?: { key: string; name: string; is_default: boolean; options: { key: string; label: string; tier?: string | null }[] }[];
}

/** One of the business's standard documents (Settings › Campaign defaults › Documents). */
export interface ShareDocument {
  key: string;
  label: string;
  url: string;
}

export const NO_CHOICES: StepChoices = { meeting_types: [], whatsapp_templates: [], price_book: null, guides: [], booking_link: false, documents: [], flows: [], books: [] };

/** The documents the email after the call can share: their quote, then the business's own. */
export function documentOptions(choices: StepChoices): { value: string; label: string }[] {
  return [{ value: "quote", label: "Their quote (once it has gone to them)" }, ...(choices.documents ?? []).map((d) => ({ value: d.key, label: d.label }))];
}

// ------------------------------------------------------------------ step words
/** What each mode does, in a few words. */
export const MODE_LABEL: Record<string, string> = {
  acknowledge: "Thank them and say when you will reply",
  booking_link: "Send the booking link",
  offer_times: "Show the next free times, with the booking link",
  confirm_booking: "They book on your site; Ogma confirms it",
  send_guide: "Send the free guide",
  send_link: "Send the booking link",
  propose_times: "Email a few times to choose from",
  one_time: "Suggest one time",
  price_book: "Price it from your price list",
  by_hand: "You price it yourself",
  actions: "Recap the actions agreed",
  highlight: "Highlight something from the call, then the actions",
  document: "Share a document, then the actions",
};

export const CHANNEL_ORDERS: { value: string; label: string; channels: Channel[] | null }[] = [
  { value: "", label: "As in Speed to lead (your default)", channels: null },
  { value: "whatsapp,email", label: "WhatsApp, then email", channels: ["whatsapp", "email"] },
  { value: "email,whatsapp", label: "Email, then WhatsApp", channels: ["email", "whatsapp"] },
  { value: "whatsapp", label: "WhatsApp only", channels: ["whatsapp"] },
  { value: "email", label: "Email only", channels: ["email"] },
];

/** "First reply" for the reply step, else the block's name. */
export function stepLabel(key: string): string {
  return key === "reply" ? "First reply" : (BLOCKS[key as BlockKey]?.label ?? key);
}

/** The steps of a path that can have settings: the first reply, then its blocks in force. */
export function stepKeys(blocks: readonly string[]): StepKey[] {
  return ["reply", ...(blocks as BlockKey[])];
}

/** "within 30 minutes", "within 4 hours", "within 2 days". */
/** The reply times a campaign picks from for its first reply (docs/specs/CAMPAIGN_STEPS_2026_11.md A1), in hours. */
export const REPLY_TIMES: readonly number[] = [0.25, 1, 4, 8, 24];

export function paceText(hours: number | undefined | null): string | null {
  if (typeof hours !== "number" || !Number.isFinite(hours) || hours <= 0) return null;
  if (hours < 1) return `within ${Math.round(hours * 60)} minutes`;
  if (hours === 1) return "within an hour";
  if (hours >= 24 && hours % 24 === 0) return hours === 24 ? "within a day" : `within ${hours / 24} days`;
  return `within ${hours} hours`;
}

/** One line for a step's settings on the path card, or null when it has none. */
export function stepSummary(key: string, step: StepSettings | undefined, choices: StepChoices = NO_CHOICES): string | null {
  if (!step || Object.keys(step).length === 0) return null;
  const parts: string[] = [];
  if (step.mode) parts.push(MODE_LABEL[step.mode] ?? step.mode);
  if (step.meeting_type) parts.push(choices.meeting_types.find((t) => t.key === step.meeting_type)?.label ?? step.meeting_type);
  if (step.times && (step.mode === "propose_times" || step.mode === "offer_times")) parts.push(step.times === 1 ? "1 time" : `${step.times} times`);
  if (step.flow) parts.push(`sends ${choices.flows?.find((f) => f.key === step.flow)?.name ?? step.flow}`);
  if (step.book) parts.push(choices.books?.find((b) => b.key === step.book)?.name ?? step.book);
  if (step.options?.length) {
    const opts = (choices.books?.find((b) => (step.book ? b.key === step.book : b.is_default))?.options ?? choices.price_book?.options) ?? [];
    parts.push(step.options.map((o) => opts.find((x) => x.key === o)?.label ?? o).join(", "));
  }
  if (step.channels?.length) parts.push(step.channels.map((c) => (c === "whatsapp" ? "WhatsApp" : "email")).join(", then "));
  if (step.whatsapp_template && step.whatsapp_template !== "enquiry_reply") parts.push(`template ${step.whatsapp_template}`);
  const pace = paceText(step.due_hours);
  if (pace) parts.push(pace);
  if (step.save_offer) parts.push(step.save_offer.enabled === false ? "no save offer" : "its own save offer");
  if (step.document && (step.mode === "document" || key === "send_document")) parts.push(documentOptions(choices).find((d) => d.value === step.document)?.label ?? step.document);
  if (key === "send_document" && step.wait_days !== undefined) parts.push(step.wait_days === 0 ? "straight away" : `after ${dayText(step.wait_days)}`);
  if (key === "make_offer" && step.wait_days !== undefined) parts.push(`after ${dayText(step.wait_days)} with no answer`);
  if (step.expires_days !== undefined) parts.push(`lasts ${dayText(step.expires_days)}`);
  if (step.chases === 0) parts.push("no chasers");
  else if (step.chases !== undefined || step.chase_days !== undefined) parts.push(`chase every ${step.chase_days ?? 5} days, up to ${step.chases ?? 2} times`);
  if (step.chase) parts.push(chaseText(step.chase));
  return parts.length ? parts.join(" · ") : key === "follow_up" ? "Its own settings" : null;
}

function dayText(n: number): string {
  return n === 1 ? "1 day" : `${n} days`;
}

export const CHASE_THEN_TEXT: Record<ChaseThen, string> = { task: "a task for you", move_on: "move on", lost: "close it as lost" };

/** "If nothing happens" in a few words: "2 chasers, then a task for you". */
export function chaseText(c: ChaseSettings): string {
  const n = c.steps?.length ?? 0;
  return `${n === 0 ? "no chasers" : n === 1 ? "1 chaser" : `${n} chasers`}, then ${CHASE_THEN_TEXT[c.then ?? "task"]}`;
}

/** What is wrong with one step's settings before saving, in plain words (null when fine). */
export function stepProblem(key: string, step: StepSettings, choices: StepChoices = NO_CHOICES): string | null {
  if (step.due_hours !== undefined && (!Number.isFinite(step.due_hours) || step.due_hours < 0.25 || step.due_hours > 720)) return "The pace must be from a quarter of an hour to 720 hours (30 days).";
  if (key === "reply" && step.mode === "send_guide" && !step.guide_url) return "Choose the guide to send, or give its web address.";
  if (step.guide_url && !GUIDE_RE.test(step.guide_url)) return "The guide's address must start with https://.";
  if (step.meeting_type && choices.meeting_types.length && !choices.meeting_types.some((t) => t.key === step.meeting_type)) return "Choose one of your meeting types.";
  if (step.options && step.options.length > 3) return "Choose up to three options from your price list.";
  if (step.flow && choices.flows && !choices.flows.some((f) => f.key === step.flow && f.purpose === (key === "qualify" ? "qualify" : "quote"))) {
    return `Choose one of your published ${key === "qualify" ? "qualifying" : "quote"} flows.`;
  }
  if (step.book && choices.books && !choices.books.some((b) => b.key === step.book)) return "Choose one of your price lists with a published version.";
  if (key === "recap" && step.mode === "document" && !step.document) return "Choose the document to share.";
  if (key === "send_document" && !step.document) return "Choose the document to send.";
  if (step.wait_days !== undefined && (!Number.isInteger(step.wait_days) || step.wait_days < 0 || step.wait_days > 60)) return "Wait 0 to 60 days (0 for straight away).";
  if (step.expires_days !== undefined && (!Number.isInteger(step.expires_days) || step.expires_days < 1 || step.expires_days > 60)) return "The offer lasts 1 to 60 days.";
  if (step.document && step.document !== "quote" && !(choices.documents ?? []).some((d) => d.key === step.document)) return "Choose one of your documents, or add it on Documents.";
  if (step.chase_days !== undefined && (!Number.isInteger(step.chase_days) || step.chase_days < 1 || step.chase_days > 30)) return "Chase every 1 to 30 days.";
  if (step.chases !== undefined && (!Number.isInteger(step.chases) || step.chases < 0 || step.chases > 5)) return "Up to 5 chasers (0 for none).";
  if (step.chase && cleanChase(step.chase) === null) return "Say what happens if nothing happens, or clear it.";
  if (step.chase?.steps?.some((c) => !Number.isInteger(c.gap_days) || c.gap_days < 0 || c.gap_days > 30)) return "Wait 0 to 30 days before each chaser.";
  if ((step.chase?.steps?.length ?? 0) > 3) return "Up to 3 chasers.";
  if (step.chase?.after_hours !== undefined && (step.chase.after_hours < 0.25 || step.chase.after_hours > 720)) return "Start the chasers a quarter of an hour (0.25) to 720 hours after they reach the step.";
  return null;
}

export type RunStatus = "active" | "waiting" | "done" | "stopped";

/** crm_connect_run */
export interface ConnectRun {
  run_id: string;
  organisation_id: string;
  organisation?: string;
  deal_id?: string | null;
  path_key: string;
  path_label: string;
  template: TemplateKey;
  template_label?: string;
  status: RunStatus;
  chosen_by: "person" | "campaign" | "inbound" | "offer" | "flavour" | "default";
  position: number;
  /** The campaign the firm came through, whichever rule picked the path. */
  play_id?: string | null;
  campaign?: string | null;
  campaign_module?: CampaignModule | null;
  block?: BlockKey | null;
  block_label?: string;
  block_kind?: "watched" | "person";
  what?: string;
  automated_in?: string | null;
  blocks: { key: BlockKey; label: string; kind: "watched" | "person"; state: "done" | "current" | "to_do" }[];
  block_done?: string | null;
  task?: { id: string; kind: string; status: string } | null;
  entered_at?: string;
  started_at: string;
  updated_at?: string;
  ended_at?: string | null;
  end_reason?: string | null;
  started_by?: string;
  config_version?: number | null;
  /** When it reached its step, when that step is due by its pace, and whether that has passed (20270219090000). */
  block_at?: string;
  due_at?: string;
  late?: boolean;
  /** Steps a route jumped over, and each route taken. */
  skipped?: BlockKey[];
  route_log?: RouteLogEntry[];
  /** The open "choose where they go next" task (9.25). */
  route_task?: string | null;
  /** The book its path's Quote step uses (Make a quote starts there), when it has a published version
   * (migration 20270224090000). */
  quote_book_id?: string;
  /** Its path's steps changed after it started (it can be moved onto them). */
  path_changed?: boolean;
  earlier?: { run_id: string; path_key: string; path_label: string; status: RunStatus; started_at: string; ended_at?: string | null; end_reason?: string | null }[];
}

/** One route a run took (connect_runs.route_log). */
export type RouteLogEntry = {
  at: string;
  from: BlockKey;
  on?: "done" | "timed_out";
  /** Its place in the path's routes; absent when a person chose. */
  rule?: number;
  to: string;
  when?: Route["when"];
  /** The facts the condition read. */
  facts?: Record<string, string | number | boolean | null>;
  skipped?: BlockKey[];
  chosen_by?: string;
}

export type StartPathResult =
  | { started: true; run_id: string; path_key: string; path_label: string; block: BlockKey; status: RunStatus; chosen_by: string }
  | { started: false; reason: string; run_id?: string };

export interface CompleteBlockResult {
  done?: boolean;
  advanced?: boolean;
  block?: BlockKey | null;
  status?: RunStatus;
  reason?: string;
}

// ------------------------------------------------------------------ words
/** Who does a block now: Ogma, Ogma watching for it, a person until a phase is built, or a person by design. */
export function blockWho(b: { kind: "watched" | "person"; automated_in?: string | null }): { tone: "ok" | "info" | "warn" | "plain"; label: string; note: string } {
  if (b.kind === "watched") {
    return b.automated_in
      ? { tone: "info", label: "Ogma watches", note: `Moves on when it happens; Ogma does more of it from ${b.automated_in}` }
      : { tone: "ok", label: "Automated", note: "Ogma moves on by itself" };
  }
  return b.automated_in
    ? { tone: "warn", label: "You, for now", note: `A one-click task for you; Ogma will do this from ${b.automated_in}` }
    : { tone: "plain", label: "You", note: "Always a person's decision" };
}

export const CHOSEN_BY_TEXT: Record<ConnectRun["chosen_by"], string> = {
  person: "chosen by a person",
  campaign: "picked by the campaign they came through",
  inbound: "your Inbound path (no campaign)",
  offer: "picked by the offer they came through",
  flavour: "picked by the way they came in",
  default: "your default path",
};

export const RUN_STATUS_TEXT: Record<RunStatus, string> = {
  active: "Ogma is watching for the next step",
  waiting: "Waiting for you",
  done: "Every step done",
  stopped: "Stopped",
};

/** Where a waiting firm's task is worked: its card on Next steps, else the firm page. */
export function waitingHref(w: Pick<WaitingRun, "task_id" | "organisation_id"> & { block?: BlockKey | null }): string {
  return w.task_id ? stepTaskHref(w.block ?? null, w.task_id) : `/contacts/companies/${w.organisation_id}`;
}

/** Where a path step's task opens: a Quote step's (prepare_quote) on Quotes, the rest on Next steps. */
export function stepTaskHref(block: BlockKey | null, task: string): string {
  return block === "quote" ? `/quotes?task=${task}` : `/conversations/next-steps?task=${task}`;
}

/** "3 of 7 steps done". */
export function runProgress(run: Pick<ConnectRun, "blocks" | "status">): { done: number; total: number } {
  const total = run.blocks.length;
  const done = run.status === "done" ? total : run.blocks.filter((b) => b.state === "done").length;
  return { done, total };
}

/** What a path is for, in a line, in the order the rules pick: its campaigns (by name when given), offers and
 * flavours, and whether it is the default. */
export function pathPicksText(p: Pick<PathSetting, "offers" | "flavours" | "default"> & { campaigns?: string[] }, campaignNames?: string[]): string {
  const parts: string[] = [];
  const nCamp = campaignNames?.length ?? p.campaigns?.length ?? 0;
  if (nCamp) parts.push(campaignNames?.length ? `campaigns: ${campaignNames.join(", ")}` : nCamp === 1 ? "1 campaign" : `${nCamp} campaigns`);
  if (p.offers.length) parts.push(`offers: ${p.offers.map((o) => OFFER_PATH_LABEL[o] ?? o).join(", ")}`);
  if (p.flavours.length) parts.push(`${p.flavours.map((f) => f[0]!.toUpperCase() + f.slice(1)).join(", ")} leads`);
  if (p.default) parts.push("everyone else (default)");
  return parts.length ? `For ${parts.join("; ")}` : "Only when a person chooses it";
}

// ------------------------------------------------------------------ cleaning and the derived paths (for demo mode)
const KEY_RE = /^[a-z][a-z0-9_]{1,40}$/;
/** A campaign id on a path, as the database keeps it (lower case); at most 50 a path (crm__clean_paths). */
const CAMPAIGN_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;
const MAX_CAMPAIGNS = 50;
/** At most this many paths (crm__clean_paths; 100 since 31 Oct 2026, so tens of campaigns each have their own steps). */
export const MAX_PATHS = 100;

export function pathBlocks(template: TemplateKey, off: readonly string[]): BlockKey[] {
  return TEMPLATE_BY_KEY[template].blocks.map((b) => b.key).filter((k) => !off.includes(k));
}

/** Steps you define (#493, migration 20270130090000; docs/specs/CONNECT.md section 19): when both blocks of a pair are on a
 * path, the first comes before the second, because a watched block waits for the one before it. Follow up comes last. */
const ORDER_RULES: readonly (readonly [BlockKey, BlockKey])[] = [
  ["book", "meet"], ["meet", "recap"], ["quote", "accept"], ["proposal", "accept"], ["accept", "pay"],
  ["sign_up", "grant_access"], ["sign_up", "nudge"], ["sign_up", "convert"],
];
const MAX_BLOCKS = 12;

const isBlock = (b: unknown): b is BlockKey => typeof b === "string" && Object.hasOwn(BLOCKS, b);

/** crm__path_order_problem: null when the engine can follow the order, else the reason for a person. */
export function orderProblem(blocks: readonly BlockKey[]): string | null {
  const pos = new Map(blocks.map((b, i) => [b, i]));
  const fu = pos.get("follow_up");
  if (fu !== undefined && fu !== blocks.length - 1) return "Follow up must be the last step";
  for (const [before, after] of ORDER_RULES) {
    const b = pos.get(before);
    const a = pos.get(after);
    if (b !== undefined && a !== undefined && b > a) return `${BLOCKS[after].label} must come after ${BLOCKS[before].label}`;
  }
  return null;
}

/** crm__own_blocks: a path's own blocks, known ones only, each once, at most 12; null when there are none or the order is
 * one the engine can't follow, so the template decides. */
export function ownBlocks(raw: unknown): BlockKey[] | null {
  if (!Array.isArray(raw)) return null;
  const out: BlockKey[] = [];
  for (const b of raw) {
    if (out.length >= MAX_BLOCKS) break;
    if (isBlock(b) && !out.includes(b)) out.push(b);
  }
  return out.length && !orderProblem(out) ? out : null;
}

/** crm__clean_paths: what the database keeps of connect.paths (supabase/tests/fixtures/paths_clean_cases.json holds the
 * cases the database, engine/paths.py and this must clean alike). A path's own blocks replace its template's; only the
 * first path that asks to be the Inbound path is. A path with usable routes carries them (routes_clean_cases.json). */
export function cleanPaths(raw: unknown): PathSetting[] {
  const out: PathSetting[] = [];
  if (!Array.isArray(raw)) return out;
  const seen = new Set<string>();
  const claimedO = new Set<string>();
  const claimedF = new Set<string>();
  const claimedC = new Set<string>();
  let hasDefault = false;
  let hasInbound = false;
  for (const item of raw) {
    if (out.length >= MAX_PATHS) break;
    if (!item || typeof item !== "object" || Array.isArray(item)) continue;
    const it = item as Record<string, unknown>;
    const key = it.key;
    const tpl = it.template;
    if (typeof key !== "string" || !KEY_RE.test(key) || seen.has(key)) continue;
    if (typeof tpl !== "string" || !(tpl in TEMPLATE_BY_KEY)) continue;
    const t = TEMPLATE_BY_KEY[tpl as TemplateKey];
    let label = typeof it.label === "string" ? it.label.trim() : "";
    if (label.length < 2 || label.length > 60) label = t.label;
    const offers = (Array.isArray(it.offers) ? it.offers : []).filter((o): o is Offer => typeof o === "string" && (OFFERS as readonly string[]).includes(o) && !claimedO.has(o));
    offers.forEach((o) => claimedO.add(o));
    const flavours = (Array.isArray(it.flavours) ? it.flavours : []).filter((f): f is Flavour => typeof f === "string" && (FLAVOURS as string[]).includes(f) && !claimedF.has(f));
    flavours.forEach((f) => claimedF.add(f));
    const campaigns: string[] = [];
    for (const c of Array.isArray(it.campaigns) ? it.campaigns : []) {
      if (typeof c === "string" && CAMPAIGN_RE.test(c) && !claimedC.has(c) && campaigns.length < MAX_CAMPAIGNS) {
        campaigns.push(c);
        claimedC.add(c);
      }
    }
    const wanted = Array.isArray(it.off) ? it.off : [];
    const own = ownBlocks(it.blocks);
    const off = own ? [] : t.blocks.filter((b) => b.optional && wanted.includes(b.key)).map((b) => b.key);
    const blocks = own ?? pathBlocks(t.key, off);
    const isDefault: boolean = it.default === true && !hasDefault;
    hasDefault ||= isDefault;
    const inbound: boolean = it.inbound === true && !hasInbound;
    hasInbound ||= inbound;
    const routes = cleanRoutes(it.routes, blocks);
    out.push({ key, label, template: t.key, offers: [...new Set(offers)], flavours: [...new Set(flavours)], campaigns, off, default: isDefault, inbound, blocks, steps: cleanSteps(it.steps, blocks), ...(routes.length ? { routes } : {}) });
    seen.add(key);
  }
  if (out.length && !hasDefault) out[0]!.default = true;
  return out;
}

/** crm__selling_template */
const SELLING_TEMPLATE: Record<SellingMethod, { template: TemplateKey; off: BlockKey[] }> = {
  call: { template: "intro_call", off: ["qualify", "proposal"] },
  proposal: { template: "intro_call", off: ["qualify"] },
  instant_price: { template: "instant_price", off: ["qualify", "pay"] },
  qa_quote: { template: "quote_from_known", off: ["pay"] },
  site_visit: { template: "survey_then_quote", off: ["pay"] },
  tender_response: { template: "tender_response", off: [] },
};

/** crm__connect_paths: the business's paths, or (derived) the ones made from how it sells. */
export function connectPaths(config: JsonObject | null | undefined): { derived: boolean; paths: PathSetting[] } {
  const connect = config?.connect;
  const own = cleanPaths(connect && typeof connect === "object" && !Array.isArray(connect) ? (connect as JsonObject).paths : null);
  if (own.length) return { derived: false, paths: own };
  const s = connectSettings(config);
  const t = SELLING_TEMPLATE[s.selling[0] ?? "call"];
  const paths: PathSetting[] = [{ key: t.template, label: TEMPLATE_BY_KEY[t.template].label, template: t.template, offers: [], flavours: [], campaigns: [], off: [...t.off], default: true, inbound: false, blocks: pathBlocks(t.template, t.off), steps: {} }];
  for (const f of FLAVOURS) {
    const tf = SELLING_TEMPLATE[s.flavours[f].selling[0] ?? "call"];
    if (tf.template === t.template && tf.off.join() === t.off.join()) continue;
    const same = paths.find((p) => p.template === tf.template && p.off.join() === tf.off.join() && !p.default);
    if (same) {
      same.flavours.push(f);
      continue;
    }
    const key = paths.some((p) => p.key === tf.template) ? `${tf.template}_${f}` : tf.template;
    paths.push({ key, label: `${TEMPLATE_BY_KEY[tf.template].label} (${f[0]!.toUpperCase()}${f.slice(1)})`, template: tf.template, offers: [], flavours: [f], campaigns: [], off: [...tf.off], default: false, inbound: false, blocks: pathBlocks(tf.template, tf.off), steps: {} });
  }
  return { derived: true, paths };
}

// ------------------------------------------------------------------ the editor
/** One path as the editor holds it: exactly the fields connect.paths accepts. */
export interface PathDraft {
  key: string;
  label: string;
  template: TemplateKey;
  offers: Offer[];
  flavours: Flavour[];
  campaigns: string[];
  off: BlockKey[];
  default: boolean;
  /** The Inbound path (#495): firms with no campaign follow it. Kept as it is when a path is saved. */
  inbound?: boolean;
  /** The path's own steps in order (#493), when they are not its template's minus the steps switched off. */
  blocks?: BlockKey[];
  /** The step settings (absent or empty: none). Only steps of blocks in force are saved. */
  steps?: PathSteps;
  /** Where a firm goes after each step (9.25). Only routes that still fit the steps in force are saved. */
  routes?: Route[];
}

export function toDraft(p: PathSetting): PathDraft {
  // A board path lists its blocks in force; they are its own only when they differ from its template's.
  const own = p.blocks && p.blocks.join() !== pathBlocks(p.template, p.off).join() ? [...p.blocks] : undefined;
  return {
    key: p.key, label: p.label, template: p.template, offers: [...p.offers], flavours: [...p.flavours], campaigns: [...(p.campaigns ?? [])],
    off: own ? [] : [...p.off], default: p.default, ...(p.inbound ? { inbound: true } : {}), ...(own ? { blocks: own } : {}),
    steps: JSON.parse(JSON.stringify(p.steps ?? {})) as PathSteps,
    ...(p.routes?.length ? { routes: JSON.parse(JSON.stringify(p.routes)) as Route[] } : {}),
  };
}

/** The steps a draft runs, in order: its own, else its template's minus the ones switched off. */
export function draftOrder(d: Pick<PathDraft, "template" | "off" | "blocks">): BlockKey[] {
  return d.blocks ?? pathBlocks(d.template, d.off);
}

/** Whether a step can move one place earlier (-1) or later (+1) in an order the engine can follow. */
export function canMove(d: PathDraft, block: BlockKey, by: -1 | 1): boolean {
  const order = draftOrder(d);
  const i = order.indexOf(block);
  const j = i + by;
  if (i < 0 || j < 0 || j >= order.length) return false;
  const next = [...order];
  [next[i], next[j]] = [next[j]!, next[i]!];
  return orderProblem(next) === null;
}

/** Moves a step one place (Book a meeting first, say): the path then keeps its own steps in order. */
export function moveBlock(drafts: PathDraft[], i: number, block: BlockKey, by: -1 | 1): PathDraft[] {
  return drafts.map((d, n) => {
    if (n !== i || !canMove(d, block, by)) return d;
    const order = [...draftOrder(d)];
    const k = order.indexOf(block);
    [order[k], order[k + by]] = [order[k + by]!, order[k]!];
    return { ...d, off: [], blocks: order };
  });
}

/** Sets (or, with null, clears) one step's settings on one path. */
export function setStep(drafts: PathDraft[], i: number, key: StepKey, step: StepSettings | null): PathDraft[] {
  return drafts.map((d, n) => {
    if (n !== i) return d;
    const steps: PathSteps = { ...(d.steps ?? {}) };
    if (step && Object.keys(step).length) steps[key] = step;
    else delete steps[key];
    return { ...d, steps };
  });
}

/** The steps of a draft that will be saved: "reply" and the blocks in force, as the database keeps them. */
export function draftSteps(d: Pick<PathDraft, "template" | "off" | "blocks" | "steps">): PathSteps {
  return cleanSteps(d.steps ?? {}, draftOrder(d));
}

/** Sets one step's routes on one path (the others' routes stay, in their order). */
export function setRoutesFrom(drafts: PathDraft[], i: number, from: BlockKey, routes: Route[]): PathDraft[] {
  return drafts.map((d, n) => (n === i ? { ...d, routes: [...(d.routes ?? []).filter((r) => r.from !== from), ...routes] } : d));
}

/** The routes of a draft that will be saved: the ones that still fit its steps in force, as the database keeps them. */
export function draftRoutes(d: Pick<PathDraft, "template" | "off" | "blocks" | "routes">): Route[] {
  return cleanRoutes(d.routes ?? [], draftOrder(d));
}

/** A board path as the editor starts it: its campaigns plus any retired ones the settings still list, so the editor
 * shows them (to untick) instead of saving them again unseen. */
export function boardDraft(p: PathSetting & { retired_campaigns?: { id: string }[] }): PathDraft {
  const d = toDraft(p);
  return { ...d, campaigns: [...d.campaigns, ...(p.retired_campaigns ?? []).map((c) => c.id).filter((id) => !d.campaigns.includes(id))] };
}

/** A key for a new path from a template that no other path uses: the template's key, then _2, _3… */
export function freshKey(template: TemplateKey, taken: string[]): string {
  if (!taken.includes(template)) return template;
  for (let n = 2; ; n++) if (!taken.includes(`${template}_${n}`)) return `${template}_${n}`;
}

/** "Add a path": a new path from a template, with every block on; the first path is the default. */
export function addPath(drafts: PathDraft[], template: TemplateKey): PathDraft[] {
  const t = TEMPLATE_BY_KEY[template];
  const labels = drafts.map((d) => d.label);
  let label = t.label;
  for (let n = 2; labels.includes(label); n++) label = `${t.label} ${n}`;
  return [...drafts, { key: freshKey(template, drafts.map((d) => d.key)), label, template, offers: [], flavours: [], campaigns: [], off: [], default: drafts.length === 0, steps: {} }];
}

/** Removes a path; if it was the default, the first left becomes the default. */
export function removePath(drafts: PathDraft[], i: number): PathDraft[] {
  const out = drafts.filter((_, n) => n !== i);
  if (out.length && !out.some((d) => d.default)) out[0] = { ...out[0]!, default: true };
  return out;
}

export function setDefault(drafts: PathDraft[], i: number): PathDraft[] {
  return drafts.map((d, n) => ({ ...d, default: n === i }));
}

/** Changes a path's template: optional blocks switched off stay off only where the new template has them as optional. */
export function setTemplate(drafts: PathDraft[], i: number, template: TemplateKey): PathDraft[] {
  const opt = TEMPLATE_BY_KEY[template].blocks.filter((b) => b.optional).map((b) => b.key);
  return drafts.map((d, n) => (n === i ? { ...d, template, off: d.off.filter((k) => opt.includes(k)), blocks: undefined } : d));
}

/** Ticks an offer, a flavour or a campaign for one path. Each leads to one path only, so ticking it here takes it off
 * any other (many campaigns may still share one path). */
export function claim<K extends "offers" | "flavours" | "campaigns">(drafts: PathDraft[], i: number, field: K, value: PathDraft[K][number], on: boolean): PathDraft[] {
  return drafts.map((d, n) => {
    const list = d[field] as string[];
    if (n === i) return { ...d, [field]: on ? (list.includes(value) ? list : [...list, value]) : list.filter((x) => x !== value) };
    return on && list.includes(value) ? { ...d, [field]: list.filter((x) => x !== value) } : d;
  });
}

/** Switches an optional block on or off. A block the template needs cannot be switched off. */
export function toggleBlock(drafts: PathDraft[], i: number, block: BlockKey, use: boolean): PathDraft[] {
  return drafts.map((d, n) => {
    if (n !== i) return d;
    const tplBlocks = TEMPLATE_BY_KEY[d.template].blocks;
    // A step the path added itself (addBlock) can always come out; a ready-made one only when the template allows.
    const optional = tplBlocks.some((b) => b.key === block && b.optional) || !tplBlocks.some((b) => b.key === block);
    if (!optional) return d;
    if (d.blocks) {
      if (!use) return { ...d, blocks: d.blocks.filter((k) => k !== block) };
      if (d.blocks.includes(block)) return d;
      // Back in, before the first step that comes after it in the template.
      const tpl = TEMPLATE_BY_KEY[d.template].blocks.map((b) => b.key);
      const at = d.blocks.findIndex((k) => tpl.indexOf(k) > tpl.indexOf(block));
      const next = [...d.blocks];
      next.splice(at < 0 ? next.length : at, 0, block);
      return { ...d, blocks: orderProblem(next) ? d.blocks : next };
    }
    return { ...d, off: use ? d.off.filter((k) => k !== block) : [...new Set([...d.off, block])] };
  });
}

/** The steps a path can add beyond the ones it has: every step not in it yet, while it has room (12 at most). */
export function addableBlocks(d: PathDraft): BlockKey[] {
  const order = draftOrder(d);
  if (order.length >= 12) return [];
  return (Object.keys(BLOCKS) as BlockKey[]).filter((k) => !order.includes(k));
}

/** Adds a step to a path: as late as it can go before Follow up (move it earlier from there), in an order the engine
 * can follow. The path then keeps its own steps. Nothing changes when there is no such place. */
export function addBlock(drafts: PathDraft[], i: number, block: BlockKey): PathDraft[] {
  return drafts.map((d, n) => {
    if (n !== i || !addableBlocks(d).includes(block)) return d;
    const order = draftOrder(d);
    const last = order[order.length - 1] === "follow_up" ? order.length - 1 : order.length;
    for (let at = block === "follow_up" ? order.length : last; at >= 0; at--) {
      const next = [...order.slice(0, at), block, ...order.slice(at)];
      if (orderProblem(next) === null) return { ...d, off: [], blocks: next };
    }
    return d;
  });
}

/** The campaign page's step builder (docs/specs/CAMPAIGN_PAGES_2026_10.md section 2.5): takes a step out of a path that
 * keeps its own steps, whichever step it is, as long as one is left. The path then keeps its own steps. */
export function removeBlock(drafts: PathDraft[], i: number, block: BlockKey): PathDraft[] {
  return drafts.map((d, n) => {
    if (n !== i) return d;
    const order = draftOrder(d);
    if (order.length < 2 || !order.includes(block)) return d;
    return { ...d, off: [], blocks: order.filter((k) => k !== block) };
  });
}

/** Moves a step to a place in the order (a drag and drop). Returns the drafts unchanged and the reason when the engine
 * can't follow the new order. */
export function placeBlock(drafts: PathDraft[], i: number, block: BlockKey, to: number): { drafts: PathDraft[]; problem: string | null } {
  const d = drafts[i];
  if (!d) return { drafts, problem: null };
  const order = draftOrder(d).filter((k) => k !== block);
  if (order.length === draftOrder(d).length) return { drafts, problem: null };
  const next = [...order.slice(0, to), block, ...order.slice(to)];
  const problem = orderProblem(next);
  if (problem) return { drafts, problem };
  return { drafts: drafts.map((x, n) => (n === i ? { ...x, off: [], blocks: next } : x)), problem: null };
}

/** A campaign's own path with just one step, made from the path it follows today (its template, step settings and reply
 * pace): the first step added to a new campaign that started with none. */
export function startOwnSteps(drafts: PathDraft[], campaign: { id: string; name: string }, sourceKey: string, block: BlockKey, replyHours: number | null, campaigns: Pick<CampaignSummary, "id" | "name">[]): PathDraft[] {
  const next = giveCampaignSteps(drafts, campaign, sourceKey, replyHours, campaigns);
  const id = campaign.id.toLowerCase();
  return next.map((d) => (d.campaigns.includes(id) ? { ...d, off: [], blocks: [block] } : d));
}

/** A campaign's own path made from a ready set of steps (Ogma's recommended steps for the sector): the template, the steps
 * in order and their settings, replacing whatever the campaign had. */
export function giveRecommendedSteps(drafts: PathDraft[], campaign: { id: string; name: string }, rec: { template: TemplateKey; blocks: BlockKey[]; steps: PathSteps }, campaigns: Pick<CampaignSummary, "id" | "name">[]): PathDraft[] {
  const id = campaign.id.toLowerCase();
  const own = { template: rec.template, off: [] as BlockKey[], blocks: [...rec.blocks], steps: JSON.parse(JSON.stringify(rec.steps)) as PathSteps, routes: undefined };
  const mine = drafts.findIndex((d) => d.campaigns.includes(id));
  if (mine >= 0 && pathAlsoFor(drafts[mine]!, campaigns, id).length === 0) return drafts.map((d, n) => (n === mine ? { ...d, ...own } : d));
  const made: PathDraft = { key: freshKey(rec.template, drafts.map((d) => d.key)), label: freshLabel(campaign.name, drafts), offers: [], flavours: [], campaigns: [], default: false, ...own };
  return claim([...drafts, made], drafts.length, "campaigns", id, true);
}

/** A path draft as the board shows a path, for the step editor while the draft is held on the page (not saved yet). */
export function draftAsBoardPath(d: PathDraft): BoardPath {
  const s = cleanPaths(toSettings([d]))[0];
  const p: PathSetting = s ?? { key: d.key, label: d.label, template: d.template, offers: d.offers, flavours: d.flavours, campaigns: d.campaigns, off: d.off, default: d.default, blocks: draftOrder(d), steps: d.steps ?? {} };
  return { ...p, default: d.default, template_label: TEMPLATE_BY_KEY[d.template].label, firms: 0, block_list: [], waiting: [] };
}

/** What is wrong before saving, in plain words (null when fine): the editor's own rules, then act's checks. */
export function pathsProblem(drafts: PathDraft[]): string | null {
  if (drafts.length === 0) return "Keep at least one path.";
  if (drafts.length > MAX_PATHS) return `You can have at most ${MAX_PATHS} paths.`;
  for (const [n, d] of drafts.entries()) {
    const label = d.label.trim();
    if (label.length < 2 || label.length > 60) return `Give path ${n + 1} a name of 2 to 60 characters.`;
    if (!KEY_RE.test(d.key)) return `Path ${n + 1} has a key Ogma can't use.`;
  }
  if (new Set(drafts.map((d) => d.label.trim().toLowerCase())).size !== drafts.length) return "Give each path a different name.";
  if (drafts.filter((d) => d.default).length !== 1) return "Choose one default path.";
  return checkSection("connect_paths", { paths: toSettings(drafts) as unknown as JsonObject[] });
}

/** What the settings change sends: connect.paths, trimmed. Step settings go only when a path has some (and only for
 * "reply" and the blocks in force), so a business that sets none saves exactly what it did before. */
export function toSettings(drafts: PathDraft[]): PathDraft[] {
  return drafts.map((d) => {
    const steps = draftSteps(d);
    const routes = draftRoutes(d);
    return {
      key: d.key, label: d.label.trim(), template: d.template, offers: [...d.offers], flavours: [...d.flavours], campaigns: [...d.campaigns],
      off: d.blocks ? [] : [...d.off], default: d.default, ...(d.inbound ? { inbound: true } : {}), ...(d.blocks ? { blocks: [...d.blocks] } : {}),
      ...(Object.keys(steps).length ? { steps } : {}),
      ...(routes.length ? { routes } : {}),
    };
  });
}

/** The campaigns grouped by module, in module order (Attract, Find, Network, ReConnect); empty modules left out. */
export function campaignsByModule(list: CampaignSummary[]): { module: CampaignModule; label: string; campaigns: CampaignSummary[] }[] {
  return CAMPAIGN_MODULES.map((m) => ({ module: m, label: CAMPAIGN_MODULE_LABEL[m], campaigns: list.filter((c) => c.module === m) })).filter((g) => g.campaigns.length > 0);
}

/** Where a campaign's firms go when no path lists it, in the order crm_start_path tries: the offer of the CTA they came
 * through, the path for their way in (Attract, Find and Network campaigns), then the default. */
export function campaignFallbackText(c: Pick<CampaignSummary, "module">, paths: Pick<PathSetting, "label" | "offers" | "flavours" | "default">[]): string {
  const deflt = paths.find((p) => p.default)?.label ?? paths[0]?.label;
  const byWay = c.module !== "reconnect" ? paths.find((p) => p.flavours.includes(c.module as Flavour)) : undefined;
  const anyOffer = paths.some((p) => p.offers.length > 0);
  const steps = [anyOffer ? "the path for the offer they answered, if any" : null, byWay ? `${byWay.label} (for ${CAMPAIGN_MODULE_LABEL[c.module]} leads)` : null, deflt ? `${deflt} (your default)` : null].filter(Boolean);
  return steps.length ? `No path of its own: falls back to ${steps.join(", then ")}.` : "No path of its own.";
}

// ------------------------------------------------------------------ campaigns first (spec #372 section 1, #496)
/** The way in a campaign's firms come by, for the flavour defaults (ReConnect has none of its own). */
export function campaignFlavour(module: CampaignModule): Flavour | null {
  return (FLAVOURS as readonly string[]).includes(module) ? (module as Flavour) : null;
}

/** Who else follows a path besides one campaign, in words: other campaigns by name, then the ways in, the offers, the
 *  Inbound enquiries and everyone else when it is the default. Empty when the campaign has the path to itself. */
export function pathAlsoFor(path: Pick<PathSetting, "campaigns" | "flavours" | "offers" | "inbound" | "default">, campaigns: Pick<CampaignSummary, "id" | "name">[], except: string | null): string[] {
  const names = path.campaigns.filter((id) => id !== except).map((id) => campaigns.find((c) => c.id.toLowerCase() === id)?.name).filter((n): n is string => Boolean(n));
  return [
    ...names,
    ...path.flavours.map((f) => `${f[0]!.toUpperCase()}${f.slice(1)} leads`),
    ...path.offers.map((o) => `people who answer ${OFFER_PATH_LABEL[o]}`),
    ...(path.inbound ? ["enquiries with no campaign"] : []),
    ...(path.default ? ["everyone with no other path (your default)"] : []),
  ];
}

/** The path a campaign follows and why: its own, shared with others, or a fallback (the way in, then the default, as
 *  crm_start_path tries when no path lists it). Null only when there are no paths. */
export function campaignPath(c: Pick<CampaignSummary, "id" | "module" | "path_key" | "falls_back_to">, paths: PathSetting[], campaigns: Pick<CampaignSummary, "id" | "name">[]): { path: PathSetting; why: "own" | "shared" | "fallback"; also: string[] } | null {
  const listed = c.path_key ? paths.find((p) => p.key === c.path_key) : undefined;
  if (listed) {
    const also = pathAlsoFor(listed, campaigns, c.id.toLowerCase());
    return { path: listed, why: also.length ? "shared" : "own", also };
  }
  const flavour = campaignFlavour(c.module);
  const fallback =
    (c.falls_back_to && paths.find((p) => p.key === c.falls_back_to!.path_key)) ??
    (flavour ? paths.find((p) => p.flavours.includes(flavour)) : undefined) ??
    paths.find((p) => p.default) ??
    paths[0];
  return fallback ? { path: fallback, why: "fallback", also: pathAlsoFor(fallback, campaigns, null) } : null;
}

/** "its own steps", "shares Intro call with 2 other campaigns", "follows Intro call, the default". */
export function campaignWhyText(c: Pick<CampaignSummary, "id" | "name" | "module" | "path_key" | "falls_back_to">, paths: PathSetting[], campaigns: Pick<CampaignSummary, "id" | "name">[]): string {
  const at = campaignPath(c, paths, campaigns);
  if (!at) return "no path yet";
  if (at.why === "own") return `${at.path.label}: its own steps`;
  if (at.why === "shared") {
    const others = at.path.campaigns.filter((id) => id !== c.id.toLowerCase()).length;
    return others ? `shares ${at.path.label} with ${others === 1 ? "1 other campaign" : `${others} other campaigns`}` : `shares ${at.path.label} with ${at.also.join(", ")}`;
  }
  const flavour = campaignFlavour(c.module);
  return `follows ${at.path.label}, ${at.path.default && !(flavour && at.path.flavours.includes(flavour)) ? "the default" : `the path for ${CAMPAIGN_MODULE_LABEL[c.module]} leads`}`;
}

/** A step's pace in words: its own due_hours, else the first reply's way-in default ("1 hour, the Attract default"),
 *  else when a person's task is due or a watched step happens. */
export function stepPaceText(key: StepKey, step: StepSettings | undefined, fallback?: { hours: number; label: string }): string {
  const own = paceText(step?.due_hours);
  if (own) return own;
  if (key === "reply") return fallback ? `${hoursText(fallback.hours)}, the ${fallback.label} default` : "the default for their way in";
  return BLOCKS[key].kind === "person" ? "due when they reach it" : "when it happens";
}

/** A copy of one path's steps, step settings and routes, with the reply step's effective pace written into it, so the
 *  copy replies as fast as the source did. */
function copiedSteps(source: PathDraft, replyHours: number | null): Pick<PathDraft, "template" | "off" | "blocks" | "steps" | "routes"> {
  const steps = JSON.parse(JSON.stringify(source.steps ?? {})) as PathSteps;
  if (replyHours !== null && steps.reply?.due_hours === undefined) steps.reply = { ...(steps.reply ?? {}), due_hours: replyHours };
  return { template: source.template, off: [...source.off], ...(source.blocks ? { blocks: [...source.blocks] } : {}), steps, ...(source.routes?.length ? { routes: JSON.parse(JSON.stringify(source.routes)) as Route[] } : {}) };
}

/** A path name no other path uses: the name, then "name 2", "name 3"… cut to 60 characters. */
function freshLabel(name: string, drafts: PathDraft[]): string {
  const base = name.trim().slice(0, 56) || "Campaign path";
  const taken = drafts.map((d) => d.label.trim().toLowerCase());
  let label = base;
  for (let n = 2; taken.includes(label.toLowerCase()); n++) label = `${base} ${n}`;
  return label;
}

/**
 * "Give this campaign its own steps" and "Copy steps from": the campaign gets the source path's steps. A campaign that
 * already has a path to itself has them replaced there; otherwise a new path, named after the campaign, is made for it
 * and the campaign is taken off any other (claim), so the campaigns it shared with keep their path as it was.
 */
export function giveCampaignSteps(drafts: PathDraft[], campaign: { id: string; name: string }, sourceKey: string, replyHours: number | null, campaigns: Pick<CampaignSummary, "id" | "name">[]): PathDraft[] {
  const source = drafts.find((d) => d.key === sourceKey);
  if (!source) return drafts;
  const id = campaign.id.toLowerCase();
  const mine = drafts.findIndex((d) => d.campaigns.includes(id));
  const copy = copiedSteps(source, replyHours);
  if (mine >= 0 && pathAlsoFor(drafts[mine]!, campaigns, id).length === 0) {
    return drafts.map((d, n) => (n === mine ? { ...d, ...copy, ...(copy.blocks ? {} : { blocks: undefined }) } : d));
  }
  const made: PathDraft = { key: freshKey(source.template, drafts.map((d) => d.key)), label: freshLabel(campaign.name, drafts), offers: [], flavours: [], campaigns: [], default: false, ...copy };
  return claim([...drafts, made], drafts.length, "campaigns", id, true);
}

/** "Set up Inbound": a copy of the path a firm with no campaign follows today (the Attract way in's, else the default),
 *  marked Inbound. */
export function setUpInbound(drafts: PathDraft[], replyHours: number | null): PathDraft[] {
  if (drafts.some((d) => d.inbound)) return drafts;
  const source = drafts.find((d) => d.flavours.includes("attract")) ?? drafts.find((d) => d.default) ?? drafts[0];
  if (!source) return drafts;
  return [...drafts, { key: freshKey(source.template, drafts.map((d) => d.key)), label: freshLabel("Inbound", drafts), offers: [], flavours: [], campaigns: [], default: false, inbound: true, ...copiedSteps(source, replyHours) }];
}

/** Whether the editor holds anything to save: a change, or (for derived paths) saving them to start editing. */
export function pathsChanged(start: PathDraft[], now: PathDraft[], derived: boolean): boolean {
  return derived || JSON.stringify(toSettings(start)) !== JSON.stringify(toSettings(now));
}

/** The blocks a draft path will go through, with whether each is on and whether it can be switched off. */
export function draftBlocks(d: PathDraft): { key: BlockKey; label: string; optional: boolean; on: boolean; info: BlockInfo }[] {
  const tpl = TEMPLATE_BY_KEY[d.template].blocks;
  const optional = (k: BlockKey) => tpl.find((b) => b.key === k)?.optional ?? true;
  const row = (k: BlockKey, on: boolean) => ({ key: k, label: BLOCKS[k].label, optional: optional(k), on, info: BLOCKS[k] });
  if (!d.blocks) return tpl.map((b) => row(b.key, !d.off.includes(b.key)));
  // Its own steps in order, then the template's optional steps it left out.
  return [...d.blocks.map((k) => row(k, true)), ...tpl.filter((b) => b.optional && !d.blocks!.includes(b.key)).map((b) => row(b.key, false))];
}

/** The templates as the settings rules know them (a test checks they match TEMPLATES). */
export const RULE_TEMPLATES = PATH_TEMPLATES;
